/**
 * Design tokens — the single source of truth for font sizes and spacing.
 *
 * Nothing in the app should hard-code a `fontSize:` number. Pull from `fs`
 * (or use a MUI Typography variant) so the whole product stays on one scale
 * and a change here flows everywhere. Standard body text is 14px.
 */

/** Font-size scale (px). `base` (14) is the app-wide standard body size. */
export const fs = {
  micro:   12,   // dense meta / timestamps — the floor, never smaller
  tiny:    12.5, // secondary labels, helper text
  small:   13,   // compact labels, chips-adjacent text
  label:   13.5, // form labels, table-ish supporting text
  base:    14,   // STANDARD body text
  md:      15,   // emphasised body / small sub-heading
  lg:      16,   // sub-heading (h3)
  icon:    17,   // inline action-icon glyphs
  xl:      18,   // section title (h2)
  h2:      20,
  title:   22,
  h1:      24,   // page title
  display: 28,   // large stat numbers
  statLg:  44,
  statXl:  48,
} as const;

/**
 * Spacing scale — multiples of the 8px grid, as MUI `sx` spacing units.
 * Use these names for page/section rhythm instead of arbitrary numbers.
 */
export const space = {
  pagePad:    3,   // outer page padding (24px)
  sectionGap: 2.5, // gap between sections (20px)
  cardPad:    2.5, // inside a card/section (20px)
  rowGap:     2,   // between rows / stacked blocks (16px)
  fieldGap:   1.5, // between form fields (12px)
  inline:     1,   // small inline gap (8px)
} as const;

/**
 * Corner radii from the wireframes. Tables and stat boxes are squarer (5px);
 * everything else follows the wireframe value for its kind.
 */
export const radius = {
  table:      '5px',  // table cards, data grids
  stat:       '5px',  // summary strips, stat / budget / period cards
  actionIcon: '5px',  // tinted row action buttons
  button:     '8px',
  buttonSm:   '6px',
  input:      '8px',
  menu:       '10px',
  notice:     '10px', // info boxes, bulk bars, entry cards
  dialog:     '12px', // dialogs, popovers
  pill:       '11px', // status / day / type pills
  chip:       '12px', // flag chips, filter chips
} as const;

/** Neutral colours shared by tables, strips and forms. */
export const ink = {
  border:   '#e1e5e8', // card and input borders
  line:     '#eef2f4', // row rules
  headBg:   '#fafbfc', // table header band
  headFg:   '#6b7785', // table header text
  hint:     '#999999', // helper text under fields
  stripVal: '#1f2a35', // numbers in summary strips
} as const;

/** Tinted row actions: blue view, cyan edit, red delete, green add, amber warn. */
export const actionTone = {
  view: { bg: '#e8f0fb', fg: '#3276e0' },
  edit: { bg: '#e5f6fb', fg: '#17b8d4' },
  del:  { bg: '#fdecea', fg: '#e0614f' },
  add:  { bg: '#edfaf3', fg: '#1bcb84' },
  warn: { bg: '#fdf4e1', fg: '#c8891a' },
} as const;

/** Outlined status pills: label colour and border. */
export const pillTone = {
  green: { fg: '#178a55', bd: '#9fd9bb' },
  amber: { fg: '#c78a14', bd: '#f0c27a' },
  blue:  { fg: '#2a64c8', bd: '#9dbcf0' },
  red:   { fg: '#e0614f', bd: '#f2b4ac' },
  grey:  { fg: '#5d6875', bd: '#d5dbe1' },
} as const;

export type FontToken = keyof typeof fs;
export type SpaceToken = keyof typeof space;

/** Soft badge from any brand/user colour: light tint, deeper text, faint border. */
export const softTone = (hex?: string | null) => {
  const c = hex || '#94a3b8';
  return {
    backgroundColor: `color-mix(in srgb, ${c} 12%, #fff)`,
    color: `color-mix(in srgb, ${c} 70%, #1f2a35)`,
    border: `1px solid color-mix(in srgb, ${c} 35%, #fff)`,
  };
};
